import React, { useState, useEffect } from 'react';
import { Send } from 'lucide-react';
import { Button } from './ui/button';
import DialCodeSelect from './DialCodeSelect';
import { getDetectedCountry, getCountryList, submitLead } from '../services/api';

const CTASection = () => {
  const countries = getCountryList();

  const [formData, setFormData] = useState({
    name: '',
    countryDialCode: 1,
    country: 'US',
    phone: '',
    email: '',
    message: '',
  });

  const [detectedCountryName, setDetectedCountryName] = useState('');
  const [geoData, setGeoData] = useState(null);
  const [rawGeo, setRawGeo] = useState(null);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');

  useEffect(() => {
    (async () => {
      const detected = await getDetectedCountry();
      setDetectedCountryName(detected.countryName);
      setGeoData(detected.geoData);
      setRawGeo(detected.rawGeo);
      setFormData(prev => ({
        ...prev,
        country: detected.countryCode,
        countryDialCode: detected.dialCode,
      }));
    })();
  }, []);

  const validateEmail = (email) => {
    const t = email.trim();
    if (!t || (t.match(/@/g) || []).length !== 1) return false;
    const [local, domain] = t.split('@');
    if (!local || !domain || !domain.includes('.')) return false;
    if (domain.startsWith('.') || domain.endsWith('.')) return false;
    if (t.includes(' ') || t.includes('..')) return false;
    return /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(t);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      setFormData(prev => ({ ...prev, phone: value.replace(/\D/g, '').substring(0, 10) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleCountryChange = (e) => {
    const dialCode = Number(e.target.value);
    const match = countries.find(c => c.dialCode === dialCode);
    setFormData(prev => ({
      ...prev,
      countryDialCode: dialCode,
      country: match ? match.code : prev.country,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    else if (!/^\d{10}$/.test(formData.phone)) newErrors.phone = 'Phone must be exactly 10 digits';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!validateEmail(formData.email)) newErrors.email = 'Please enter a valid email address';
    if (!formData.message.trim()) newErrors.message = 'Message is required';

    if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }

    setApiError('');
    setIsSubmitting(true);

    const result = await submitLead({
      name: formData.name,
      email: formData.email,
      phoneNumber: formData.phone,
      countryDialCode: formData.countryDialCode,
      country: formData.country,
      description: formData.message,
      geoData,
      rawGeo,
    });

    setIsSubmitting(false);

    if (result.success) {
      window.location.href = result.redirectUrl;
    } else {
      setApiError(result.error);
    }
  };

  return (
    <div className="relative py-20 text-white overflow-hidden">
      <img src="https://images.unsplash.com/photo-1507679799987-c73779587ccf?w=900&q=50" alt="" loading="eager" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-br from-[#2F1A8E]/90 via-[#1a0f5e]/85 to-[#0d0830]/90"></div>
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#FF035B]/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#FF035B]/5 rounded-full blur-2xl"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Protect Your Brand?</h2>
          <p className="text-base text-gray-300 max-w-2xl mx-auto">Fill out the form below and our trademark experts will get back to you within 24 hours</p>
          {detectedCountryName && (
            <p className="text-sm text-white/70 mt-2" data-testid="cta-detected-country">
              Detected location: {detectedCountryName}
            </p>
          )}
        </div>

        {apiError && (
          <div className="bg-red-500/10 border border-red-500 text-red-300 px-4 py-3 rounded-lg mb-4 text-center max-w-2xl mx-auto" data-testid="cta-api-error">
            <p className="text-sm">{apiError}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate className="w-full" data-testid="cta-form">
          <div className="flex flex-col lg:flex-row gap-3 mb-3 items-start">
            {/* Name */}
            <div className="flex-1">
              <input type="text" name="name" value={formData.name} onChange={handleChange} className={`w-full h-12 px-4 py-3 bg-white/10 border ${errors.name ? 'border-red-500' : 'border-white/20'} rounded-lg text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-[#FF035B]`} placeholder="Your Name *" data-testid="cta-name-input" />
              {errors.name && <p className="text-red-400 text-xs mt-1">{errors.name}</p>}
            </div>

            {/* Phone */}
            <div className="flex-1">
              <div className="flex items-center gap-1">
                <DialCodeSelect
                  value={formData.countryDialCode}
                  onChange={(dialCode) => handleCountryChange({ target: { value: dialCode } })}
                  countries={countries}
                  variant="dark"
                  testId="cta-dialcode-select"
                />
                <input type="tel" name="phone" value={formData.phone} onChange={handleChange} onKeyDown={(e) => { if (!/[0-9]/.test(e.key) && !['Backspace','Delete','Tab','ArrowLeft','ArrowRight','Home','End'].includes(e.key) && !e.ctrlKey && !e.metaKey) e.preventDefault(); }} maxLength="10" inputMode="numeric" pattern="[0-9]*" className={`flex-1 h-12 px-4 py-3 bg-white/10 border ${errors.phone ? 'border-red-500' : 'border-white/20'} rounded-lg text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-[#FF035B]`} placeholder="Phone Number *" data-testid="cta-phone-input" />
              </div>
              {errors.phone && <p className="text-red-400 text-xs mt-1">{errors.phone}</p>}
            </div>

            {/* Email */}
            <div className="flex-1">
              <input type="email" name="email" value={formData.email} onChange={handleChange} className={`w-full h-12 px-4 py-3 bg-white/10 border ${errors.email ? 'border-red-500' : 'border-white/20'} rounded-lg text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-[#FF035B]`} placeholder="Your Email *" data-testid="cta-email-input" />
              {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
            </div>

            {/* Message */}
            <div className="flex-1">
              <input type="text" name="message" value={formData.message} onChange={handleChange} className={`w-full h-12 px-4 py-3 bg-white/10 border ${errors.message ? 'border-red-500' : 'border-white/20'} rounded-lg text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-[#FF035B]`} placeholder="What are you looking to protect? (e.g. Name, Logo and Slogan)" data-testid="cta-message-input" />
              {errors.message && <p className="text-red-400 text-xs mt-1">{errors.message}</p>}
            </div>

            {/* Submit */}
            <div>
              <Button type="submit" disabled={isSubmitting} className="w-full lg:w-auto h-12 bg-gradient-to-r from-[#FF035B] to-[#2F1A8E] hover:from-[#2F1A8E] hover:to-[#FF035B] text-white font-bold px-8 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg shadow-[#FF035B]/30 disabled:opacity-50 whitespace-nowrap" data-testid="cta-submit-btn">
                {isSubmitting ? 'Submitting...' : (<>Start Registration <Send className="w-4 h-4 ml-2 inline" /></>)}
              </Button>
            </div>
          </div>

          <p className="text-sm text-gray-400 text-center mt-4">No credit card required &bull; Free consultation &bull; Money-back guarantee</p>
        </form>
      </div>
    </div>
  );
};

export default CTASection;
